import focus from "@alpinejs/focus";
import intersect from "@alpinejs/intersect";
import type { Alpine } from "alpinejs";
import { createAnalyticsHeader } from "./utils/createAnalyticsHeader";

interface Grecaptcha {
	ready(cb: () => void): void;
	execute(siteKey: string, options: { action: string }): Promise<string>;
	render(el: HTMLElement, options: Record<string, unknown>): number;
}

interface AlpineComponent {
	$nextTick(): Promise<void>;
}

const CAPTCHA_ACTIONS = {
	LANDING_CONTACT_US: "landing_contact_us",
	LANDING_REQUEST_DEMO: "landing_request_demo",
} as const;
const backendUrl = import.meta.env.PUBLIC_BACKEND_URL ?? "";
const siteKeyV3 = import.meta.env.PUBLIC_RECAPTCHA_SITE_KEY_V3 ?? "";
const siteKeyV2 = import.meta.env.PUBLIC_RECAPTCHA_SITE_KEY_V2 ?? "";

function ensureRecaptchaScript(): void {
	if (!siteKeyV3 || document.getElementById("recaptcha-v3-script")) return;

	const hasForm =
		document.querySelector("[x-data='contactForm']") ||
		document.querySelector("[x-data^='demoModal']");

	if (!hasForm) return;

	const s = document.createElement("script");
	s.id = "recaptcha-v3-script";
	s.src = `https://www.google.com/recaptcha/api.js?render=${siteKeyV3}`;
	s.async = true;
	document.head.appendChild(s);
}

ensureRecaptchaScript();
document.addEventListener("astro:page-load", ensureRecaptchaScript);

function getV3Token(action: string): Promise<string | null> {
	if (!siteKeyV3) return Promise.resolve(null);

	return new Promise((resolve) => {
		const timeout = setTimeout(() => resolve(null), 5000);

		const tryExecute = () => {
			const grecaptcha = (window as Window & { grecaptcha?: Grecaptcha })
				.grecaptcha;
			if (!grecaptcha) {
				clearTimeout(timeout);
				resolve(null);
				return;
			}

			grecaptcha.ready(() => {
				grecaptcha
					.execute(siteKeyV3, { action })
					.then((token) => {
						clearTimeout(timeout);
						resolve(token);
					})
					.catch(() => {
						clearTimeout(timeout);
						resolve(null);
					});
			});
		};

		const script = document.getElementById("recaptcha-v3-script");
		if (
			script &&
			!(window as Window & { grecaptcha?: Grecaptcha }).grecaptcha
		) {
			script.addEventListener("load", tryExecute, { once: true });
		} else {
			tryExecute();
		}
	});
}

export default (Alpine: Alpine) => {
	Alpine.plugin(intersect);
	Alpine.plugin(focus);

	Alpine.data("contactForm", () => ({
		fields: { name: "", company: "", workEmail: "", message: "", website: "" },
		errors: {} as Record<string, string>,
		isSubmitting: false,
		status: null as string | null,
		showV2: false,
		v2WidgetId: null as number | null,

		validate(): boolean {
			this.errors = {};
			const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

			if (this.fields.name.trim().length < 2)
				this.errors.name = "Name is required";
			if (this.fields.company.trim().length < 2)
				this.errors.company = "Company is required";
			if (!emailReg.test(this.fields.workEmail.trim()))
				this.errors.workEmail = "Valid work email is required";

			return Object.keys(this.errors).length === 0;
		},

		async submit() {
			if (!this.validate() || this.isSubmitting) return;

			this.isSubmitting = true;
			this.status = null;

			try {
				const v3Token = await getV3Token(CAPTCHA_ACTIONS.LANDING_CONTACT_US);
				await this.sendRequest(v3Token, null);
			} catch {
				this.status = "error";
			} finally {
				this.isSubmitting = false;
			}
		},

		async sendRequest(v3Token: string | null, v2Token: string | null) {
			const bodyStr = JSON.stringify({
				name: this.fields.name,
				company: this.fields.company,
				workEmail: this.fields.workEmail,
				website: this.fields.website || undefined, // honeypot field
				message: this.fields.message || undefined,
				captchaTokenV2: v2Token ?? undefined,
				captchaTokenV3: v3Token,
			});

			const res = await fetch(`${backendUrl}/landing/contact-us`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"x-analytics": await createAnalyticsHeader(),
				},
				body: bodyStr,
			});

			const data = await res.json();

			if (data?.code === "1020") {
				this.showV2 = true;
				await (this as unknown as AlpineComponent).$nextTick();
				this.renderV2();

				return;
			}

			if (!res.ok) throw new Error("Request failed");

			this.fields = {
				name: "",
				company: "",
				workEmail: "",
				message: "",
				website: "",
			};
			this.errors = {};
			this.showV2 = false;
			this.v2WidgetId = null;

			window.dispatchEvent(
				new CustomEvent("show-toast", {
					detail: {
						message: "Message sent! We'll get back to you soon.",
						type: "success",
					},
				}),
			);
		},

		renderV2() {
			const el = document.getElementById("contact-recaptcha-v2");
			const grecaptcha = (window as Window & { grecaptcha?: Grecaptcha })
				.grecaptcha;

			if (!el || !grecaptcha || !siteKeyV2 || this.v2WidgetId !== null) return;

			this.v2WidgetId = grecaptcha.render(el, {
				sitekey: siteKeyV2,
				theme: "dark",
				action: CAPTCHA_ACTIONS.LANDING_CONTACT_US,
				callback: (token: string) => this.submitWithV2(token),
				"expired-callback": () => {
					this.v2WidgetId = null;
				},
			});
		},

		async submitWithV2(v2Token: string) {
			this.isSubmitting = true;

			try {
				const v3Token = await getV3Token(CAPTCHA_ACTIONS.LANDING_CONTACT_US);
				await this.sendRequest(v3Token, v2Token);
			} catch {
				this.status = "error";
			} finally {
				this.isSubmitting = false;
			}
		},
	}));

	Alpine.data("demoModal", (modalId: string) => ({
		isOpen: false,
		fields: {
			fullName: "",
			companyName: "",
			businessEmail: "",
			planOfInterest: "",
			message: "",
			website: "",
		},
		errors: {} as Record<string, string>,
		isSubmitting: false,
		status: null as string | null,
		showV2: false,
		v2WidgetId: null as number | null,
		_listeners: null as (() => void) | null,

		init() {
			const onOpen = (e: Event) => {
				if ((e as CustomEvent).detail?.id !== modalId) return;

				const scrollbarWidth =
					window.innerWidth - document.documentElement.clientWidth;
				document.body.style.overflow = "hidden";
				document.body.style.paddingRight = `${scrollbarWidth}px`;
				this.isOpen = true;
			};
			const onKeydown = (e: Event) => {
				if ((e as KeyboardEvent).key === "Escape" && this.isOpen) this.close();
			};

			window.addEventListener("open-modal", onOpen);
			window.addEventListener("keydown", onKeydown);

			this._listeners = () => {
				window.removeEventListener("open-modal", onOpen);
				window.removeEventListener("keydown", onKeydown);
			};
		},

		destroy() {
			this._listeners?.();
		},

		close() {
			this.isOpen = false;
			document.body.style.overflow = "";
			document.body.style.paddingRight = "";
			setTimeout(() => {
				this.fields = {
					fullName: "",
					companyName: "",
					businessEmail: "",
					planOfInterest: "",
					message: "",
					website: "",
				};
				this.errors = {};
				this.status = null;
				this.showV2 = false;
				this.v2WidgetId = null;
			}, 200);
		},

		validate(): boolean {
			this.errors = {};
			const emailReg = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

			if (this.fields.fullName.trim().length < 2)
				this.errors.fullName = "Full name is required";
			if (this.fields.companyName.trim().length < 2)
				this.errors.companyName = "Company name is required";
			if (!emailReg.test(this.fields.businessEmail.trim()))
				this.errors.businessEmail = "Valid business email is required";
			if (!this.fields.planOfInterest)
				this.errors.planOfInterest = "Please select a plan";

			return Object.keys(this.errors).length === 0;
		},

		async submit() {
			if (!this.validate() || this.isSubmitting) return;

			this.isSubmitting = true;
			this.status = null;

			try {
				const v3Token = await getV3Token(CAPTCHA_ACTIONS.LANDING_REQUEST_DEMO);
				await this.sendRequest(v3Token, null);
			} catch {
				this.status = "error";
			} finally {
				this.isSubmitting = false;
			}
		},

		async sendRequest(v3Token: string | null, v2Token: string | null) {
			const bodyStr = JSON.stringify({
				fullName: this.fields.fullName,
				companyName: this.fields.companyName,
				businessEmail: this.fields.businessEmail,
				planOfInterest: this.fields.planOfInterest,
				message: this.fields.message || undefined,
				website: this.fields.website || undefined, // honeypot field
				captchaTokenV2: v2Token ?? undefined,
				captchaTokenV3: v3Token,
			});
			const hashBuffer = await crypto.subtle.digest(
				"SHA-256",
				new TextEncoder().encode(bodyStr),
			);
			const hashHex = Array.from(new Uint8Array(hashBuffer))
				.map((b) => b.toString(16).padStart(2, "0"))
				.join("");

			const res = await fetch(`${backendUrl}/landing/request-demo`, {
				method: "POST",
				body: bodyStr,
				headers: {
					"Content-Type": "application/json",
					"x-amz-content-sha256": hashHex,
					"x-analytics": await createAnalyticsHeader(),
				},
			});

			const data = await res.json();

			if (data?.code === "1020") {
				this.showV2 = true;
				await (this as unknown as AlpineComponent).$nextTick();
				this.renderV2();

				return;
			}

			if (!res.ok) throw new Error("Request failed");

			this.close();
			window.dispatchEvent(
				new CustomEvent("show-toast", {
					detail: {
						message: "Request sent! We'll get back to you within 24 hours.",
						type: "success",
					},
				}),
			);
		},

		renderV2() {
			const el = document.getElementById("demo-recaptcha-v2");
			const grecaptcha = (window as Window & { grecaptcha?: Grecaptcha })
				.grecaptcha;

			if (!el || !grecaptcha || !siteKeyV2 || this.v2WidgetId !== null) return;

			this.v2WidgetId = grecaptcha.render(el, {
				sitekey: siteKeyV2,
				theme: "dark",
				action: CAPTCHA_ACTIONS.LANDING_REQUEST_DEMO,
				callback: (token: string) => this.submitWithV2(token),
				"expired-callback": () => {
					this.v2WidgetId = null;
				},
			});
		},

		async submitWithV2(v2Token: string) {
			this.isSubmitting = true;

			try {
				const v3Token = await getV3Token(CAPTCHA_ACTIONS.LANDING_REQUEST_DEMO);
				await this.sendRequest(v3Token, v2Token);
			} catch {
				this.status = "error";
			} finally {
				this.isSubmitting = false;
			}
		},
	}));
};
